
import Vue from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import { debounce, DebouncedFunc, upperFirst } from 'lodash'
import { getIconFromMime } from '~/modules/utils'

interface IData {
  value: string
  type: 'any' | 'file' | 'directory' | 'text' | 'audio' | 'video' | 'image' | 'zip'
  sizes: string[]
  lastSeen: string

  loading: boolean
  payload: any
  page: number
  search: DebouncedFunc<() => Promise<void>> | null
  searched: boolean
}

export default Vue.extend({
  filters: {
    upperFirst(value: string) {
      return upperFirst(value)
    }
  },

  data: (): IData => ({
    value: '',
    sizes: [],
    lastSeen: 'now-24h/h TO *',

    loading: false,
    payload: null,
    page: 0,
    search: null,
    type: 'any',
    searched: false
  }),

  computed: {
    pageCount(): number {
      // ipfs-search.com only allows searching up to page 100
      return Math.min(this.payload.page_count - 1, 100)
    },

    searchQuery(): string {
      let query = this.value

      switch (this.type) {
        case 'text':
          query += ' metadata.Content-Type:("application/x-mobipocket-ebook" OR "application/epub+zip" OR "application/vnd.amazon.ebook" OR "image/vnd.djvu" OR "application/pdf" OR "text/html" OR "text/plain" OR "application/postscript" OR "application/rtf" OR "application/vnd.oasis.opendocument.text" OR "application/vnd.sun.xml.writer" OR "application/vnd.stardivision.writer" OR "application/x-starwriter" OR "application/msword" OR "application/vnd.openxmlformats-officedocument.wordprocessingml.document" OR "application/x-abiword")'
          break
        case 'audio':
          query += ' metadata.Content-Type:(audio*)'
          break
        case 'video':
          query += ' metadata.Content-Type:(video*)'
          break
        case 'image':
          query += ' metadata.Content-Type:(image*)'
          break
        case 'zip':
          query += ' metadata.Content-Type:("application/gzip" OR "application/zip")'
          break
      }

      for (const size of this.sizes) {
        query += ` size:${size}`
      }

      if (this.lastSeen !== 'any') {
        query += ` last-seen:[${this.lastSeen}]`
      }

      return query
    },

    searchType(): string {
      switch (this.type) {
        case 'text':
        case 'audio':
        case 'video':
        case 'image':
        case 'zip':
          return 'file'

        default:
          return this.type
      }
    }
  },

  watch: {
    page() {
      if (this.search) {
        this.search()
      }
    },

    type() {
      if (this.search) {
        this.search()
      }
    },

    sizes() {
      if (this.search) {
        this.search()
      }
    },

    lastSeen() {
      if (this.search) {
        this.search()
      }
    }
  },

  created() {
    this.search = debounce(this._search, 500)
  },

  methods: {
    normalizePage() {
      let normalized = this.page
      normalized = Math.max(normalized, 0)
      normalized = Math.min(normalized, 100)

      if (this.page !== normalized) {
        this.page = normalized
      }
    },

    async _search() {
      if (this.value.length === 0) {
        return
      }

      try {
        this.loading = true

        this.normalizePage()

        const response = await axios.get('https://api.ipfs-search.com/v1/search', {
          params: {
            q: this.searchQuery,
            type: this.searchType,
            page: this.page
          }
        })

        this.payload = response.data
        this.searched = true
      } catch (err: any) {
        Swal.fire({
          title: 'A problem has occurred',
          text: err.message,
          icon: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    /**
     * Returns the icon according to the file type.
     */
    getIcon(mimetype?: string): string {
      return getIconFromMime(mimetype)
    }
  }
})
